/**
 * All Foundations — search the whole grantmaker corpus, not just your matches.
 *
 * homelab-kb #60 Phase 2a, ADR-0012. Pro+ only: the server's require_tier gate
 * is authoritative, so a Growth client gets a 403 and this page renders the
 * upgrade prompt rather than an error alert.
 *
 * The page deliberately shows foundations the digest would never send, each
 * carrying `why_not`. That is not a caveat bolted on — it is the feature.
 * Search that surfaced tens of thousands of foundations beside a digest of 8
 * would make the digest look broken rather than selective, and "why isn't this
 * one in my digest?" is a question no competitor can answer, because none of
 * them hold an opinion about this client.
 */

import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { searchCorpus } from '../api'
import Button from '../components/Button'
import CorpusFunderCard from '../components/CorpusFunderCard'
import Spinner from '../components/Spinner'
import { ErrorAlert } from '../components/Alert'
import { FOCUS_AREA_OPTIONS, US_STATES } from '../options'
import { useDocumentTitle } from '../components/PublicLayout'

const DEBOUNCE_MS = 350

const EMPTY_FILTERS = {
  q: '', state: '', category: '', min_grant: '', max_grant: '',
  open_only: false, sort: 'relevance',
}

const SORT_OPTIONS = [
  { value: 'relevance',  label: 'Best fit for your mission' },
  { value: 'name',       label: 'Name (A–Z)' },
  { value: 'grant_size', label: 'Largest typical grant' },
  { value: 'recent',     label: 'Most recent filing' },
]

/** Shown when the server refuses on tier (403), never as a generic error. */
function UpgradePrompt() {
  return (
    <div className="mt-6 rounded-xl border border-brand-100 bg-brand-50 p-8 text-center">
      <h2 className="text-lg font-semibold text-gray-900">
        Searching every foundation is a Pro feature
      </h2>
      <p className="mt-2 text-sm text-gray-600 max-w-lg mx-auto">
        Your plan already includes searching the foundations matched to you.
        Pro adds the rest of the corpus: every grantmaker on record, ranked
        against your mission, and each one able to tell you why it is or isn't
        in your weekly digest.
      </p>
      <div className="mt-5 flex items-center justify-center gap-3">
        <Link to="/plans">
          <Button variant="primary" size="sm">See Pro</Button>
        </Link>
        <Link to="/grants/foundations"
          className="text-sm text-brand-500 hover:underline">
          Search your matches instead
        </Link>
      </div>
    </div>
  )
}

export default function CorpusSearch() {
  useDocumentTitle('All Foundations')

  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [needsUpgrade, setNeedsUpgrade] = useState(false)
  const debounce = useRef(null)

  function set(field, value) {
    setFilters(f => ({ ...f, [field]: value }))
    setPage(1) // a changed filter invalidates the current page number
  }

  useEffect(() => {
    setLoading(true)
    setError('')
    clearTimeout(debounce.current)
    debounce.current = setTimeout(() => {
      searchCorpus({ ...filters, open_only: filters.open_only || '', page })
        .then(result => { setData(result); setNeedsUpgrade(false) })
        .catch(err => {
          // 403 is a TIER refusal, not a failure — render the upsell.
          // 402 (subscription ended) is handled globally and must not be
          // mistaken for it: that client holds Pro, they just lapsed.
          if (err.status === 403) setNeedsUpgrade(true)
          else setError(err.message || 'Could not search foundations.')
        })
        .finally(() => setLoading(false))
    }, DEBOUNCE_MS)
    return () => clearTimeout(debounce.current)
  }, [filters, page])

  const total = data?.total ?? 0
  const pageSize = data?.page_size ?? 20
  // The server caps how deep paging may go (anti-scrape). Never offer a page
  // it will refuse to serve — a Next button that returns nothing reads as a bug.
  const reachable = Math.min(total, 500)
  const pageCount = Math.max(1, Math.ceil(reachable / pageSize))
  const filtersActive = Object.keys(EMPTY_FILTERS).some(
    k => filters[k] !== EMPTY_FILTERS[k]
  )

  if (needsUpgrade) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <header className="mb-2">
          <h1 className="text-2xl font-bold text-gray-900">All Foundations</h1>
        </header>
        <UpgradePrompt />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">All Foundations</h1>
        <p className="mt-1 text-sm text-gray-500">
          Every grantmaker we hold an IRS filing for, not just the ones matched
          to you. Each result says whether it's in your digest, and if not, why.
        </p>
      </header>

      <ErrorAlert message={error} />

      <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <input
          type="search"
          value={filters.q}
          onChange={e => set('q', e.target.value)}
          placeholder="Search by foundation name…"
          aria-label="Search by foundation name"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
            focus:outline-none focus:ring-2 focus:ring-brand-300"
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">State</span>
            <select value={filters.state} onChange={e => set('state', e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
              <option value="">Any state</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">Focus area</span>
            <select value={filters.category} onChange={e => set('category', e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
              <option value="">Any focus</option>
              {FOCUS_AREA_OPTIONS.map(o => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">Sort</span>
            <select value={filters.sort} onChange={e => set('sort', e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">
              Typical grant at least ($)
            </span>
            <input type="number" min="0" value={filters.min_grant}
              onChange={e => set('min_grant', e.target.value)} placeholder="5000"
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">
              …and at most ($)
            </span>
            <input type="number" min="0" value={filters.max_grant}
              onChange={e => set('max_grant', e.target.value)} placeholder="100000"
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
          </label>
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={filters.open_only}
              onChange={e => set('open_only', e.target.checked)}
              className="rounded border-gray-300" />
            Only foundations accepting applications
          </label>

          {filtersActive && (
            <button
              onClick={() => { setFilters(EMPTY_FILTERS); setPage(1) }}
              className="text-xs text-brand-500 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </section>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {loading
            ? 'Searching…'
            : `${total.toLocaleString()} foundation${total === 1 ? '' : 's'}`}
        </p>
        {pageCount > 1 && !loading && (
          <p className="text-xs text-gray-500">Page {page} of {pageCount}</p>
        )}
      </div>

      {/* Honest about the depth cap rather than silently serving nothing. */}
      {!loading && total > reachable && (
        <p className="mt-1 text-xs text-gray-400">
          Showing the first {reachable.toLocaleString()} of{' '}
          {total.toLocaleString()}. Narrow the filters to reach the rest.
        </p>
      )}

      {loading && !data ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : total === 0 ? (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-8 text-center">
          <p className="text-sm text-gray-600">
            No foundations match these filters.
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {filtersActive
              ? 'Try clearing a filter. With none set, the whole corpus is ranked against your mission.'
              : 'No grantmakers are loaded yet. This is a data problem, not a search one: the IRS filing import has not run.'}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 space-y-2">
            {data.results.map(funder => (
              <CorpusFunderCard key={funder.funder_id} funder={funder} />
            ))}
          </div>

          {pageCount > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="secondary" size="sm" disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}>
                ← Previous
              </Button>
              <span className="text-sm text-gray-500">{page} / {pageCount}</span>
              <Button variant="secondary" size="sm" disabled={page >= pageCount}
                onClick={() => setPage(p => Math.min(pageCount, p + 1))}>
                Next →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
